import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  usePostSourceTables,
  getProfilesById,
  getGetProfilesByIdQueryKey,
  getGetAvailableSourceTablesQueryKey,
} from '@/api/control/control-plane/control-plane';
import { WizardStepper } from '@/components/profiles/wizard/WizardStepper';
import { getWizardSteps, WIZARD_STEP_IDS } from '@/components/profiles/wizard/wizardSteps';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';
import { LoadingButton } from '@/components/LoadingButton';
import { useError } from '@/contexts/ErrorContext';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  SourceTablePicker,
  type SelectedTableConfig,
} from '@/components/profiles/sources/SourceTablePicker';
import { useOrgs } from '@/contexts/OrgsContext';
import { TableBatchFailureAlert } from '@/components/profiles/sources/TableBatchFailureAlert';
import { runWithConcurrency } from '@/lib/runWithConcurrency';
import {
  buildAddSourceTableRequest,
  retainSelection,
  SOURCE_TABLE_CREATE_CONCURRENCY,
  type FailedTable,
} from '@/lib/sourceTableBatch';

/**
 * Step 4: Select Tables
 * Final step in the profile creation wizard
 */
export default function SelectTablesStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const { orgId, profileId } = useParams<{ orgId: string; profileId: string }>();

  if (!orgId || !profileId) {
    logger.error(new Error('Missing orgId or profileId in route params'), {
      context: 'SelectTablesStep.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const wizardSteps = getWizardSteps(WIZARD_STEP_IDS.TABLES);

  const [selectedTables, setSelectedTables] = useState<Map<string, SelectedTableConfig>>(new Map());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [batchFailure, setBatchFailure] = useState<{
    addedCount: number;
    totalCount: number;
    failures: FailedTable[];
  } | null>(null);
  const isSubmitting = progress !== null;

  // Fetch profile to verify it exists and has source + target
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
    queryFn: async () => {
      const response = await getProfilesById(orgId, profileId);
      return response;
    },
    retry: 1,
  });

  const source = profileData?.data.source;
  const sourceId = source?.id;
  // The target's table prefix constrains which tables can be selected (128-char identifier limit).
  const tablePrefix = profileData?.data.target?.tablePrefix ?? undefined;

  // Server-driven per-org capability: whether the "select all" control is offered (issue #451).
  // Resolved server-side from the Auth org feature `sync.allowAllTables` and returned on the org
  // settings DTO. Read via a localized cast until the control API client is regenerated to include
  // the field (run `npm run gen:api:control` against a live backend).
  const { orgSettings } = useOrgs();
  const canSelectAllTables = (orgSettings as { allowAllTables?: boolean } | null)?.allowAllTables ?? false;

  // Redirect based on what's configured - auto-navigate to the first incomplete step
  useEffect(() => {
    if (!isLoadingProfile && profileData?.data) {
      if (!profileData.data.source) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`);
      } else if (!profileData.data.target) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`);
      }
      // Note: We allow users to reach tables step even if tables exist,
      // as they might want to add more tables
    } else if (!isLoadingProfile && !profileData?.data) {
      void navigate(`/dashboard/orgs/${orgId}/profiles/wizard/profile`);
    }
  }, [isLoadingProfile, profileData, navigate, orgId, profileId]);

  const { mutateAsync: createSourceTable } = usePostSourceTables();

  /**
   * Submits the selected tables with bounded concurrency so a large "select all" batch does not
   * flood the API. Every table runs even when some fail; on a partial failure the step stays put
   * with only the failed tables still selected, so a retry never re-submits tables already added.
   * Initial sync is intentionally NOT triggered here — the wizard has a dedicated
   * RunInitialSyncStep that the user proceeds to after configuring quiet time and the delete mode.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous validation errors
    setValidationError(null);
    setBatchFailure(null);

    if (!sourceId) {
      setValidationError(t.wizard.tables.sourceNotFound);
      return;
    }

    // Require at least one table to be selected
    if (selectedTables.size === 0) {
      setValidationError(t.wizard.tables.pleaseSelectAtLeastOne);
      return;
    }

    const entries = Array.from(selectedTables.entries());
    setProgress({ done: 0, total: entries.length });

    const outcomes = await runWithConcurrency(
      entries,
      SOURCE_TABLE_CREATE_CONCURRENCY,
      ([logicalName, config]) => createSourceTable({
        orgId,
        profileId,
        sourceId,
        data: buildAddSourceTableRequest(logicalName, config, source.$type),
      }),
      (done, total) => { setProgress({ done, total }); },
    );

    const failures: FailedTable[] = [];
    for (const outcome of outcomes) {
      if (!outcome.ok) {
        const { message } = handleApiError(
          outcome.error,
          { context: 'SelectTablesStep.createSourceTable', profile: profileData?.data },
          { showToast: false },
        );
        failures.push({ logicalName: outcome.item[0], message });
      }
    }
    const addedCount = entries.length - failures.length;
    setProgress(null);

    if (addedCount > 0) {
      logger.trackEvent('SourceTables.Created', {
        orgId,
        profileId,
        sourceId,
        tableCount: addedCount,
      });

      // Invalidate so anything else watching the source-tables list refetches, and so tables
      // added in this batch drop out of the picker's available list.
      void queryClient.invalidateQueries({
        queryKey: ['source-tables', sourceId],
      });
      void queryClient.invalidateQueries({
        queryKey: getGetAvailableSourceTablesQueryKey(orgId, profileId, sourceId),
      });
    }

    if (failures.length > 0) {
      setSelectedTables(retainSelection(selectedTables, failures.map((f) => f.logicalName)));
      setBatchFailure({ addedCount, totalCount: entries.length, failures });
      return;
    }

    toast.success(t.wizard.tables.sourceTablesAddedSuccessfully);
    void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/quiet-time`);
  };

  if (isLoadingProfile) {
    return (
      <div className="space-y-6">
        <WizardStepper steps={wizardSteps} />
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wizard Stepper */}
      <WizardStepper steps={wizardSteps} />

      {/* Page Header */}
      <div className="mx-auto text-center">
        <h1 className="text-3xl font-bold">{t.wizard.tables.title}</h1>
        <p className="text-muted-foreground">{t.wizard.tables.description}</p>
      </div>

      {/* Form Card */}
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>{t.wizard.tables.cardTitle}</CardTitle>
          <CardDescription>{t.wizard.tables.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6">
            {sourceId && (
              <SourceTablePicker
                orgId={orgId}
                profileId={profileId}
                sourceId={sourceId}
                tablePrefix={tablePrefix}
                value={selectedTables}
                onChange={setSelectedTables}
                scrollAreaClassName="h-[500px]"
                selectAllEnabled={canSelectAllTables}
                strings={{
                  tablesLabel: t.wizard.tables.tablesLabel,
                  selectedSuffix: t.wizard.tables.selected,
                  selectAllLabel: t.wizard.tables.selectAll,
                  deselectAllLabel: t.wizard.tables.deselectAll,
                  searchPlaceholder: t.wizard.tables.searchPlaceholder,
                  loadingMessage: t.wizard.tables.loadingTables,
                  noTablesFound: t.wizard.tables.noTablesFound,
                  noTablesAvailable: t.wizard.tables.noTablesAvailable,
                  timestampFieldLabel: t.wizard.tables.timestampField,
                  timestampFieldPlaceholder: t.wizard.tables.timestampFieldDescription,
                  timestampFieldNone: t.wizard.tables.timestampFieldNone,
                  incrementalSyncVia: t.wizard.tables.incrementalSyncVia,
                  fullReplication: t.wizard.tables.fullReplication,
                  noTimestampFieldNotice: t.wizard.tables.noTimestampFieldNotice,
                  nameTooLongForPrefix: t.wizard.tables.nameTooLongForPrefix,
                }}
              />
            )}

            {/* Partial batch failure */}
            {batchFailure && (
              <TableBatchFailureAlert
                addedCount={batchFailure.addedCount}
                totalCount={batchFailure.totalCount}
                failures={batchFailure.failures}
              />
            )}

            {/* Validation Error */}
            {validationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-4">
              <LoadingButton type="submit" disabled={isSubmitting || selectedTables.size === 0} isLoading={isSubmitting}>
                {progress
                  ? t.sources.tableBatch.progress
                      .replace('{done}', String(progress.done))
                      .replace('{total}', String(progress.total))
                  : t.wizard.tables.nextConfigureQuietTime}
              </LoadingButton>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
